import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { GeocodingControllerService } from '../api/index.ts';
import './MapPickerModal.css';

// Fix default marker icon (webpack/CRA strips default icon paths)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const DEFAULT_CENTER = [52.0, 19.5]; // Poland center
const DEFAULT_ZOOM = 6;

// Direct Nominatim reverse geocoding (fallback when backend is unavailable)
async function reverseGeocodeNominatim(lat, lon) {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=pl&zoom=16`,
    { headers: { 'User-Agent': 'TournamentSearchTool/1.0' } }
  );
  if (!res.ok) return null;
  return res.json();
}

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng);
    },
  });
  return null;
}

// Fixes gray map in modals — Leaflet needs to recalculate container size
function InvalidateSize() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 100);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
}

function RecenterMap({ lat, lon, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lon != null) {
      map.setView([lat, lon], zoom || 13);
    }
  }, [lat, lon, zoom, map]);
  return null;
}

function FlyToPoint({ lat, lon }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lon != null) {
      map.flyTo([lat, lon], 15, { duration: 1 });
    }
  }, [lat, lon, map]);
  return null;
}

function MapPickerModal({ isOpen, onConfirm, onCancel, initialLat, initialLon, initialAddress }) {
  const [markerPos, setMarkerPos] = useState(null);
  const [address, setAddress] = useState('');
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const [customNameError, setCustomNameError] = useState('');
  const searchDebounceRef = useRef(null);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (initialLat != null && initialLon != null) {
        setMarkerPos({ lat: initialLat, lng: initialLon });
        setAddress(initialAddress || '');
      } else {
        setMarkerPos(null);
        setAddress('');
      }
      setSearchQuery('');
      setSearchResults([]);
      setShowResults(false);
      setFlyTarget(null);
      setUsedSearchTerm(initialAddress || '');
      setCustomNameError('');
      setTimeout(() => setMapReady(true), 50);
    } else {
      setMapReady(false);
    }
  }, [isOpen, initialLat, initialLon, initialAddress]);

  // Search address handler
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (value.trim().length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    setIsSearching(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const results = await GeocodingControllerService.search(value.trim());
        setSearchResults(results || []);
        setShowResults(true);
      } catch {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value.trim())}&format=json&accept-language=pl&limit=5`,
            { headers: { 'User-Agent': 'TournamentSearchTool/1.0' } }
          );
          const data = await res.json();
          setSearchResults((data || []).map(d => ({ displayName: d.display_name, latitude: parseFloat(d.lat), longitude: parseFloat(d.lon) })));
          setShowResults(true);
        } catch {
          setSearchResults([]);
        }
      }
      setIsSearching(false);
    }, 400);
  }, []);

  const [usedSearchTerm, setUsedSearchTerm] = useState('');

  const handleSearchSelect = useCallback((result) => {
    setMarkerPos({ lat: result.latitude, lng: result.longitude });
    setAddress(result.displayName);
    setUsedSearchTerm('');
    setCustomNameError('');
    setFlyTarget({ lat: result.latitude, lon: result.longitude });
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  }, [searchQuery]);

  // Close search results on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMapClick = useCallback(async (latlng) => {
    setMarkerPos(latlng);
    setUsedSearchTerm('');
    setCustomNameError('');
    setAddress('');
    setIsGeocoding(true);
    try {
      // Try backend first
      const result = await GeocodingControllerService.reverse(latlng.lat, latlng.lng);
      if (result && result.displayName) {
        setAddress(result.displayName);
        return;
      }
    } catch {
      // Backend unavailable — fall through to direct Nominatim call
    }
    try {
      // Fallback: call Nominatim directly
      const data = await reverseGeocodeNominatim(latlng.lat, latlng.lng);
      if (data && data.display_name) {
        setAddress(data.display_name);
        return;
      }
    } catch {
      // ignore
    }
    setAddress(`${latlng.lat.toFixed(5)}, ${latlng.lng.toFixed(5)}`);
  }, []);

  // Always clear isGeocoding after address is set
  useEffect(() => {
    if (address !== '') {
      setIsGeocoding(false);
    }
  }, [address]);

  const handleConfirm = () => {
    if (!usedSearchTerm.trim()) {
      setCustomNameError('Wpisz wyświetlany adres boiska');
      return;
    }
    if (markerPos && address) {
      onConfirm({
        latitude: markerPos.lat,
        longitude: markerPos.lng,
        displayName: address,
        searchTerm: usedSearchTerm || '',
      });
    }
  };

  if (!isOpen) return null;

  const center = (initialLat != null && initialLon != null)
    ? [initialLat, initialLon]
    : DEFAULT_CENTER;
  const zoom = (initialLat != null && initialLon != null) ? 13 : DEFAULT_ZOOM;

  return (
    <div className="map-picker-overlay" onClick={onCancel}>
      <div className="map-picker-content" onClick={(e) => e.stopPropagation()}>
        <div className="map-picker-header">
          <h2>Wybierz lokalizację na mapie</h2>
          <button className="map-picker-close-btn" onClick={onCancel}>&times;</button>
        </div>
        <div className="map-picker-body">
          <div className="map-picker-search" ref={searchContainerRef}>
            <input
              type="text"
              className="map-picker-search-input"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Wyszukaj adres lub miasto..."
              autoComplete="off"
            />
            {isSearching && <span className="map-picker-search-spinner">Szukam...</span>}
            {showResults && searchResults.length > 0 && (
              <ul className="map-picker-search-results">
                {searchResults.map((r, idx) => (
                  <li key={idx} onClick={() => handleSearchSelect(r)}>
                    {r.displayName}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="map-picker-map-container">
            {mapReady && (
              <MapContainer
                center={center}
                zoom={zoom}
                style={{ width: '100%', height: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <InvalidateSize />
                <MapClickHandler onMapClick={handleMapClick} />
                {initialLat != null && initialLon != null && !flyTarget && (
                  <RecenterMap lat={initialLat} lon={initialLon} zoom={13} />
                )}
                {flyTarget && <FlyToPoint lat={flyTarget.lat} lon={flyTarget.lon} />}
                {markerPos && <Marker position={[markerPos.lat, markerPos.lng]} />}
              </MapContainer>
            )}
          </div>
          <div className={`map-picker-address ${isGeocoding ? 'loading' : ''}`}>
            {isGeocoding
              ? 'Pobieranie adresu...'
              : address || 'Kliknij na mapie lub wyszukaj adres powyżej'}
          </div>
          {markerPos && !isGeocoding && (
            <div className="map-picker-custom-name">
              <label className="map-picker-custom-name-label">Wyświetlany adres boiska (miejscowość, ulica, numer)</label>
              <input
                type="text"
                className={`map-picker-custom-name-input${customNameError ? ' input-error' : ''}`}
                value={usedSearchTerm}
                onChange={(e) => { setUsedSearchTerm(e.target.value); setCustomNameError(''); }}
                placeholder="Wpisz nazwę, która będzie wyświetlana..."
              />
              {customNameError && <div className="map-picker-custom-name-error">{customNameError}</div>}
            </div>
          )}
        </div>
        <div className="map-picker-footer">
          <button className="map-picker-btn-cancel" onClick={onCancel}>
            Anuluj
          </button>
          <button
            className="map-picker-btn-confirm"
            onClick={handleConfirm}
            disabled={!markerPos || isGeocoding || !address}
          >
            Zatwierdź
          </button>
        </div>
      </div>
    </div>
  );
}

export default MapPickerModal;
